/**
 * otpService.js
 */

const SPRING_API_BASE =
  (typeof process !== "undefined" && process.env?.REACT_APP_API_BASE_URL) ||
  "https://prodapi-v2.cetasika.com";

function getAuthHeader() {
  const fromAuthToken  = sessionStorage.getItem("auth_token");
  const fromCamel      = sessionStorage.getItem("authToken");
  const fromVideoToken = sessionStorage.getItem("video_token");

  const raw = fromAuthToken || fromCamel || fromVideoToken || null;

  if (!raw) {
    console.error(
      "❌ OTP: No auth token found in sessionStorage.\n" +
      "  Checked keys: auth_token, authToken, video_token\n" +
      "  All sessionStorage keys:", Object.keys(sessionStorage).join(", ")
    );
    return null;
  }

  if (!fromAuthToken && !fromCamel) {
    console.warn(
      "⚠️ OTP: Falling back to video_token for Authorization header.\n" +
      "  Make sure AppointmentJoin.jsx stores the Spring Boot JWT under 'auth_token'."
    );
  }

  return raw.startsWith("Bearer ") ? raw : `Bearer ${raw}`;
}

/**
 * Patient — fetch the OTP for an appointment.
 * @param {number|string} appointmentId
 * @returns {Promise<string>}
 */
export async function fetchOtp(appointmentId) {
  const auth = getAuthHeader();
  if (!auth) throw new Error("No auth token found. Cannot fetch OTP. Check sessionStorage 'auth_token'.");

  console.log(`📡 fetchOtp appointmentId=${appointmentId}`);

  const resp = await fetch(
    `${SPRING_API_BASE}/api/appointments/get-otp/${appointmentId}`,
    {
      method: "GET",
      headers: {
        Authorization: auth,
        "Content-Type": "application/json",
      },
    }
  );

  if (!resp.ok) {
    const text = await resp.text().catch(() => resp.statusText);
    throw new Error(`Failed to fetch OTP (${resp.status}): ${text}`);
  }

  const data  = await resp.text();
  const clean = data.trim().replace(/^"|"$/g, "");
  console.log(`✅ fetchOtp received: "${clean}"`);
  return clean;
}

/**
 * Doctor — verify the OTP entered by the doctor.
 * @param {number|string} appointmentId
 * @param {string} otp
 * @returns {Promise<{ success: boolean, message: string, data: boolean }>}
 */
export async function verifyOtp(appointmentId, otp) {
  const auth = getAuthHeader();
  if (!auth) throw new Error("No auth token found. Cannot verify OTP. Check sessionStorage 'auth_token'.");

  const url = `${SPRING_API_BASE}/api/appointments/verify-otp/${appointmentId}?otp=${encodeURIComponent(otp)}`;
  console.log(`📡 verifyOtp appointmentId=${appointmentId} otp=${otp}`);

  const resp = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: auth,
      "Content-Type": "application/json",
    },
  });

  const text = await resp.text().catch(() => "");

  if (!resp.ok) {
    console.error(`❌ verifyOtp HTTP ${resp.status}:`, text);

    // Try to parse error body for a meaningful message
    try {
      const parsed = JSON.parse(text);
      const errMsg =
        (typeof parsed.error   === "string" && parsed.error)   ||
        (typeof parsed.message === "string" && parsed.message) ||
        `OTP verification failed (${resp.status})`;
      throw new Error(errMsg);
    } catch (parseErr) {
      if (parseErr.message !== text) throw parseErr;
    }

    throw new Error(`OTP verification failed (${resp.status}): ${text || resp.statusText}`);
  }

  // ── HTTP 200 ─────────────────────────────────────────────────────────────

  // Empty body → treat as success
  if (!text.trim()) {
    console.log("✅ verifyOtp: 200 with empty body — treating as success");
    return { success: true, data: true, message: "Verified" };
  }

  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    // Non-JSON 200 body → treat as success
    console.log("✅ verifyOtp: 200 with non-JSON body — treating as success:", text);
    return { success: true, data: true, message: text };
  }

  console.log("✅ verifyOtp response:", parsed);

  // ── SUCCESS shapes ────────────────────────────────────────────────────────
  // { "success": "Appointment verified successfully" }  ← your API
  // { "success": true }                                 ← legacy
  // { "data": true }                                    ← legacy
  if (
    parsed.error === undefined &&          // no error key present
    parsed.success !== undefined &&        // success key exists
    parsed.success !== false              // not explicitly false
  ) {
    return {
      success: true,
      data: true,
      message: typeof parsed.success === "string" ? parsed.success : "Verified",
    };
  }

  // Also accept legacy { data: true } or { success: true } boolean shapes
  if (parsed.data === true || parsed.success === true) {
    return { success: true, data: true, message: parsed.message || "Verified" };
  }

  // ── FAILURE shapes ────────────────────────────────────────────────────────
  // { "error": "some message" }  ← your API
  // { "success": false, "message": "..." }
  const errMsg =
    (typeof parsed.error   === "string" && parsed.error)   ||
    (typeof parsed.message === "string" && parsed.message) ||
    "Invalid OTP. Please check and try again.";

  return { success: false, data: false, message: errMsg };
}